import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(undefined);

const SESSION_TIMEOUT = {
  DEFAULT: 10 * 60 * 1000, // 10 minutes d'inactivité
  REMEMBERED: 60 * 60 * 1000, // 1 heure d'inactivité
};

const SESSION_STORAGE_KEYS = {
  EXPIRES_AT: "app_session_expires_at",
  REMEMBER: "app_session_remember",
};

const getAuthErrorMessage = (error) => {
  const message = error?.message?.toLowerCase?.() || "";

  if (message.includes("email not confirmed")) {
    return "Vous devez valider votre adresse email avant de pouvoir vous connecter.";
  }

  if (
    message.includes("invalid login credentials") ||
    message.includes("invalid email or password")
  ) {
    return "Ce compte n’existe pas ou les identifiants sont incorrects.";
  }

  if (
    message.includes("user already registered") ||
    message.includes("already registered")
  ) {
    return "Un compte existe déjà avec cette adresse email.";
  }

  if (message.includes("password should be at least")) {
    return "Le mot de passe est trop court.";
  }

  if (
    message.includes("body is disturbed or locked") ||
    message.includes("failed to fetch") ||
    message.includes("network") ||
    message.includes("fetch")
  ) {
    return "Une erreur est survenue pendant la connexion. Veuillez réessayer.";
  }

  return "Une erreur est survenue. Veuillez réessayer.";
};

const isEmailConfirmationError = (error) => {
  const message = error?.message?.toLowerCase?.() || "";
  return message.includes("email not confirmed");
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

const getStoredValue = (key) => {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch (error) {
    console.error("Erreur localStorage.getItem:", error);
    return null;
  }
};

const setStoredValue = (key, value) => {
  try {
    window.localStorage?.setItem(key, value);
  } catch (error) {
    console.error("Erreur localStorage.setItem:", error);
  }
};

const removeStoredValue = (key) => {
  try {
    window.localStorage?.removeItem(key);
  } catch (error) {
    console.error("Erreur localStorage.removeItem:", error);
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const expirationTimerRef = useRef(null);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        console.error(
          "Erreur lors du chargement du profil:",
          error.message,
        );
        return null;
      }

      return data;
    } catch (error) {
      console.error(
        "Erreur fetchProfile:",
        error?.message || error,
      );
      return null;
    }
  }, []);

  /**
   * Durée de session choisie par l'utilisateur.
   *
   * false => 10 minutes d'inactivité
   * true  => 1 heure d'inactivité
   */
  const getSessionDuration = useCallback(() => {
    const remember =
      getStoredValue(
        SESSION_STORAGE_KEYS.REMEMBER,
      ) === "true";

    return remember
      ? SESSION_TIMEOUT.REMEMBERED
      : SESSION_TIMEOUT.DEFAULT;
  }, []);

  /**
   * Supprime uniquement notre gestion de session applicative.
   */
  const clearAppSession = useCallback(() => {
    removeStoredValue(SESSION_STORAGE_KEYS.EXPIRES_AT);
    removeStoredValue(SESSION_STORAGE_KEYS.REMEMBER);

    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }
  }, []);

  const expireSession = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error(
        "Erreur expiration session:",
        error?.message || error,
      );
    } finally {
      clearAppSession();
      setSession(null);
      setUser(null);
      setProfile(null);
    }
  }, [clearAppSession]);

  /**
   * Programme l'expiration automatique.
   */

  const scheduleSessionExpiration = useCallback(
    (expiresAt) => {
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
      }

      const remaining = expiresAt - Date.now();

      if (remaining <= 0) {
        expireSession();
        return;
      }

      expirationTimerRef.current = setTimeout(() => {
        expirationTimerRef.current = null;
        expireSession();
      }, remaining);
    },
    [expireSession],
  );

  /**
   * Repousse l'expiration après une activité utilisateur.
   */
  const updateSessionActivity = useCallback(() => {
    const expiresAt =
      Date.now() + getSessionDuration();

    setStoredValue(
      SESSION_STORAGE_KEYS.EXPIRES_AT,
      String(expiresAt),
    );

    scheduleSessionExpiration(expiresAt);
  }, [
    getSessionDuration,
    scheduleSessionExpiration,
  ]);

  /**
   * Initialise ou restaure l'expiration de la session.
   */
  const initializeSessionExpiration = useCallback(() => {
    const storedExpiresAt = Number(
      getStoredValue(
        SESSION_STORAGE_KEYS.EXPIRES_AT,
      ),
    );

    if (
      storedExpiresAt &&
      storedExpiresAt > Date.now()
    ) {
      scheduleSessionExpiration(storedExpiresAt);
      return;
    }

    updateSessionActivity();
  }, [
    scheduleSessionExpiration,
    updateSessionActivity,
  ]);

  const refreshUser = useCallback(async () => {
    try {
      setLoading(true);

      const {
        data: { session: currentSession },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error(
          "Erreur session:",
          sessionError.message,
        );

        clearAppSession();
        setSession(null);
        setUser(null);
        setProfile(null);

        return;
      }

      if (!currentSession?.user) {
        clearAppSession();
        setSession(null);
        setUser(null);
        setProfile(null);

        return;
      }

      setSession(currentSession);
      setUser(currentSession.user);

      const profileData = await fetchProfile(
        currentSession.user.id,
      );

      setProfile(profileData);

      initializeSessionExpiration();
    } catch (error) {
      console.error(
        "Erreur refreshUser:",
        error?.message || error,
      );

      clearAppSession();
      setSession(null);
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [
    fetchProfile,
    initializeSessionExpiration,
    clearAppSession,
  ]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      await refreshUser();
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        if (!mounted) return;

        setSession(currentSession || null);
        setUser(currentSession?.user || null);

        if (!currentSession?.user) {
          clearAppSession();
          setProfile(null);
          setLoading(false);
        }
      },
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();

      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
        expirationTimerRef.current = null;
      }
    };
  }, [
    refreshUser,
    clearAppSession,
  ]);

  /**
   * Toute activité utilisateur repousse
   * l'expiration de la session.
   *
   * On limite les mises à jour à une fois par seconde.
   */
  useEffect(() => {
    if (!session) return;

    let lastActivity = 0;

    const registerActivity = () => {
      const now = Date.now();

      if (now - lastActivity < 1000) {
        return;
      }

      lastActivity = now;

      updateSessionActivity();
    };

    const events = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      window.addEventListener(
        event,
        registerActivity,
        { passive: true },
      );
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(
          event,
          registerActivity,
        );
      });
    };
  }, [session, updateSessionActivity]);

  /**
   * Connexion
   *
   * rememberMe = false
   * => 10 minutes d'inactivité
   *
   * rememberMe = true
   * => 1 heure d'inactivité
   */
  const signIn = async (
    email,
    password,
    rememberMe = false,
  ) => {
    try {
      setLoading(true);

      const cleanEmail =
        email?.trim().toLowerCase();

      const { data, error } =
        await supabase.auth.signInWithPassword({
          email: cleanEmail,
          password,
        });

      if (error) {
        return {
          success: false,
          error: getAuthErrorMessage(error),
          emailConfirmationRequired:
            isEmailConfirmationError(error),
        };
      }

      setStoredValue(
        SESSION_STORAGE_KEYS.REMEMBER,
        rememberMe ? "true" : "false",
      );

      const expiresAt =
        Date.now() +
        (rememberMe
          ? SESSION_TIMEOUT.REMEMBERED
          : SESSION_TIMEOUT.DEFAULT);

      setStoredValue(
        SESSION_STORAGE_KEYS.EXPIRES_AT,
        String(expiresAt),
      );

      if (data.user) {
        const profileData = await fetchProfile(data.user.id);

        setProfile(profileData);

        scheduleSessionExpiration(expiresAt);

        return {
          success: true,
          user: data.user,
          profile: profileData,
          emailConfirmationRequired: false,
        };
      }

      return {
        success: true,
        user: null,
        profile: null,
        emailConfirmationRequired: false,
      };
    } catch (error) {
      console.error(
        "Erreur signIn:",
        error?.message || error,
      );

      return {
        success: false,
        error:
          "Une erreur est survenue pendant la connexion. Veuillez réessayer.",
        emailConfirmationRequired: false,
      };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (formData) => {
    try {
      setLoading(true);

      const { data, error } =
        await supabase.auth.signUp({
          email: formData.email
            .trim()
            .toLowerCase(),
          password: formData.password,
          options: {
            data: {
              first_name:
                formData.first_name?.trim() || "",
              last_name:
                formData.last_name?.trim() || "",
              phone:
                formData.phone?.trim() || "",
              newsletter:
                !!formData.newsletter,
            },
            emailRedirectTo:
              `${window.location.origin}/auth/callback`,
          },
        });

      if (error) {
        return {
          success: false,
          error: getAuthErrorMessage(error),
        };
      }

      return {
        success: true,
        user: data.user,
        session: data.session,
        emailConfirmationRequired:
          !data.session,
        message: data.session
          ? "Compte créé avec succès."
          : "Compte créé avec succès. Vous devez confirmer votre email avant de vous connecter.",
      };
    } catch (error) {
      console.error(
        "Erreur signUp:",
        error?.message || error,
      );

      return {
        success: false,
        error:
          "Erreur lors de l’inscription. Veuillez réessayer.",
      };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);

      const { error } =
        await supabase.auth.signOut();

      if (error) {
        return {
          success: false,
          error: getAuthErrorMessage(error),
        };
      }

      clearAppSession();

      setSession(null);
      setUser(null);
      setProfile(null);

      return { success: true };
    } catch (error) {
      console.error(
        "Erreur signOut:",
        error?.message || error,
      );

      return {
        success: false,
        error: "Erreur de déconnexion.",
      };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) {
        return {
          success: false,
          error: "Utilisateur non connecté",
        };
      }

      const { data, error } = await supabase
        .from("profiles")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select()
        .single();

      if (error) {
        return {
          success: false,
          error:
            "Impossible de mettre à jour le profil.",
        };
      }

      setProfile(data);

      return {
        success: true,
        profile: data,
      };
    } catch (error) {
      console.error(
        "Erreur updateProfile:",
        error?.message || error,
      );

      return {
        success: false,
        error:
          "Erreur de mise à jour du profil.",
      };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } =
        await supabase.auth.resetPasswordForEmail(
          email.trim().toLowerCase(),
          {
            redirectTo:
              `${window.location.origin}/reset-password`,
          },
        );

      if (error) {
        return {
          success: false,
          error: getAuthErrorMessage(error),
        };
      }

      return {
        success: true,
        message:
          "Email de réinitialisation envoyé.",
      };
    } catch (error) {
      console.error(
        "Erreur resetPassword:",
        error?.message || error,
      );

      return {
        success: false,
        error: "Erreur de réinitialisation.",
      };
    }
  };

  const resendConfirmationEmail = async (
    email,
  ) => {
    try {
      const cleanEmail =
        email?.trim().toLowerCase();

      if (!cleanEmail) {
        return {
          success: false,
          error: "Adresse email invalide.",
        };
      }

      const { error } =
        await supabase.auth.resend({
          type: "signup",
          email: cleanEmail,
          options: {
            emailRedirectTo:
              `${window.location.origin}/auth/callback`,
          },
        });

      if (error) {
        return {
          success: false,
          error:
            "Impossible de renvoyer l'email de confirmation.",
        };
      }

      return {
        success: true,
        message:
          "Email de confirmation renvoyé.",
      };
    } catch (error) {
      return {
        success: false,
        error:
          "Une erreur est survenue lors du renvoi de l'email.",
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        role: profile?.role || "client",
        loading,
        isAuthenticated: !!session,
        isAdmin:
          profile?.role === "admin" &&
          profile?.is_active === true,
        signIn,
        signUp,
        signOut,
        updateProfile,
        resetPassword,
        resendConfirmationEmail,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;